import React, { useState, useCallback } from 'react';
import {
  Button,
  Header,
  Subtitle,
  ApiTable,
  Thumbnail,
} from '../../../../components/Common';
import {
  ButtonsContainer,
  Container,
  Wrapper,
} from '../../../../components/Layout';
import { __ } from '../../../../services/translation';

import { InteractiveTableSchema } from '../../../../utils/table-shcema-utils';
import { FieldType, profileTableSchema } from 'profilpol-tables';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../reducers';
import { ProductType, Currency } from 'profilpol-types';
import {
  faPencilAlt,
  faTrash,
  faCaretDown,
  faCopy,
  faMoneyBillWave,
} from '@fortawesome/free-solid-svg-icons';
import { modal, notifications, apiTable } from '../../../../actions';
import { ItemDetails, Confirmation, BulkPriceChange } from '../../../../modals';
import ApiService from '../../../../services/api-service';
import Details from './Item';
import { formatProductPrice } from '../../../../utils/format-price';
import './ProfileList.scss';

const List = () => {
  const dispatch = useDispatch();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback((ids: string[]) => {
    setSelectedIds((prev) => {
      const allSelected = ids.every((id) => prev.has(id));
      if (allSelected) {
        return new Set();
      } else {
        return new Set(ids);
      }
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const tableData = useSelector(
    (state: ApplicationState) => state.apiTable.items
  );
  const allCurrentIds = (tableData || []).map((item: any) => item.id);

  const scheme = new InteractiveTableSchema(profileTableSchema)
    .modifyFieldStatic('weight', {
      name: 'profile.weight',
      field: 'weight',
      type: FieldType.Custom,
      customRender: (value: any, field: any, all: any) => `${value} kg/m`,
    })
    .modifyFieldStatic('price', {
      name: 'profile.price',
      field: 'price',
      type: FieldType.Custom,
      customRender: (value: any, field: any, all: any) =>
        formatProductPrice(value, Currency.PLN, ProductType.PROFILE),
    })
    .addFieldAtTheBeginning({
      type: FieldType.Custom,
      name: '',
      field: 'checkbox',
      mobile: true,
      customRender: (value: any, field: any, values: any) => {
        return (
          <div className="profile-list-checkbox" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selectedIds.has(values.id)}
              onChange={() => toggleSelect(values.id)}
            />
          </div>
        );
      },
    })
    .addFieldAtTheBeginning({
      type: FieldType.Custom,
      name: '',
      field: 'thumbnail',
      mobile: true,
      customRender: (value: any, field: any, values: any) => {
        if (values.image) {
          return (
            <Thumbnail src={`${ApiService.url}product/image/${values.id}`} />
          );
        }
      },
    })
    .addFieldAfter('price', {
      type: FieldType.Custom,
      name: 'profile.price_per_kg_short',
      field: 'pricePerKg',
      customRender: (value: any, field: any, values: any) => {
        if (value !== null && value !== undefined) {
          return `${Number(value).toFixed(2).replace('.', ',')} zł/kg`;
        }
        return '—';
      },
    })
    .addFieldAtTheEnd({
      type: FieldType.Custom,
      name: '',
      field: 'actions',
      mobile: true,
      customRender: (value: any, field: any, values: any) => {
        return (
          <ButtonsContainer tabs>
            <Button
              reversed
              small
              leftIcon
              mobileIconOnly
              faIcon={faPencilAlt}
              text="application.edit"
              to={`/panel/profiles/edit/${values.id}`}
            />
            <Button
              reversed
              small
              leftIcon
              mobileIconOnly
              faIcon={faCopy}
              text="application.duplicate"
              to={`/panel/profiles/duplicate/${values.id}`}
            />
            <Button
              reversed
              small
              leftIcon
              mobileIconOnly
              danger
              faIcon={faTrash}
              text="application.delete"
              click={() => confirmDelete(values.id)}
            />
            <Button
              reversed
              small
              leftIcon
              mobileIconOnly
              primary
              faIcon={faCaretDown}
              text="application.more"
              click={() =>
                dispatch(
                  modal.showModal(
                    <ItemDetails
                      title={values.name}
                      content={<Details id={values.id} />}
                    />
                  )
                )
              }
            />
          </ButtonsContainer>
        );
      },
    });
  const total = useSelector(
    (state: ApplicationState) => state.apiTable.countTotal
  );

  const confirmDelete = (productId: string) => {
    dispatch(
      modal.showModal(
        <Confirmation
          text="profiles.confirm_delete"
          confirmCallback={() => deleteProduct(productId)}
        />
      )
    );
  };

  const deleteProduct = async (productId: string) => {
    await ApiService.callFetch('DELETE', `product/${productId}`, () => {
      dispatch(notifications.successNotification('profiles.deleted'));
      dispatch(apiTable.refreshApiTable());
      dispatch(modal.hideModal());
    });
  };

  const openBulkPriceModal = () => {
    dispatch(
      modal.showModal(
        <BulkPriceChange
          selectedIds={Array.from(selectedIds)}
          onSuccess={clearSelection}
        />
      )
    );
  };

  return (
    <Container>
      <Wrapper>
        <Header
          header="profiles.title"
          buttonText="profiles.button_text"
          buttonTo="/panel/profiles/new"
        />
      </Wrapper>
      <Wrapper column>
        <Subtitle>
          {__('profiles.subtitle')}: <strong>{total}</strong>
        </Subtitle>
        <div className="profile-list-toolbar">
          <div className="profile-list-toolbar__actions">
            {allCurrentIds.length > 0 && (
              <Button
                small
                reversed
                click={() => toggleSelectAll(allCurrentIds)}
                text={
                  allCurrentIds.every((id: string) => selectedIds.has(id))
                    ? 'profiles.deselect_all'
                    : 'profiles.select_all'
                }
              />
            )}
            {selectedIds.size > 0 && (
              <>
                <span className="profile-list-toolbar__count">
                  {__('profiles.selected')}: <strong>{selectedIds.size}</strong>
                </span>
                <Button
                  small
                  primary
                  leftIcon
                  faIcon={faMoneyBillWave}
                  click={openBulkPriceModal}
                  text="profiles.bulk_price_button"
                />
                <Button
                  small
                  danger
                  click={clearSelection}
                  text="profiles.clear_selection"
                />
              </>
            )}
          </div>
        </div>
      </Wrapper>
      <ApiTable
        scheme={scheme}
        apiEndpointSubUrl={`product/list/${ProductType.PROFILE}`}
      />
    </Container>
  );
};

export default List;
